/**
 * Utility to clean up test wallet data
 *
 * Deletes all wallet records (expenses, earnings, usage, transactions)
 * that have the "test example" tag — through the cleanupTestWalletLedger
 * callable, since the wallet is server-write-only — plus the tagged chat
 * messages (still a client write).
 *
 * Usage (from browser console when logged in):
 *   import { cleanupTestWalletData } from './utils/cleanupTestWalletData';
 *   await cleanupTestWalletData();
 *
 * Or use the global function:
 *   await window.__cleanupTestWalletData();
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { deleteMessagesByTag } from '../repositories/conversationHistoryRepository';
import { getAuth } from 'firebase/auth';
import { GLOBAL_CHAT_HISTORY_KEY } from '../components/GlobalChatSystem/types';

const TEST_TAG = 'test example';

/** What the cleanupTestWalletLedger callable answers (functions/src/wallet/personalWallet.ts). */
interface CleanupLedgerResult {
  expenses: number;
  earnings: number;
  usage: number;
  transactions: number;
  total: number;
}

export interface CleanupResult {
  expenses: number;
  earnings: number;
  usage: number;
  transactions: number;
  messages: number;
  total: number;
  error?: string;
}

/**
 * Options for cleanup function
 */
export interface CleanupOptions {
  /** Callback invoked after messages are deleted with remaining messages for immediate UI update */
  onMessagesCleared?: (remainingMessages: any[]) => void;
}

/**
 * Clean up all wallet records with the "test example" tag for the current user
 * @param options Optional callbacks for immediate UI updates
 */
export async function cleanupTestWalletData(options?: CleanupOptions): Promise<CleanupResult> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    const result: CleanupResult = {
      expenses: 0,
      earnings: 0,
      usage: 0,
      transactions: 0,
      messages: 0,
      total: 0,
      error: 'No user logged in. Please sign in first.',
    };
    console.error('❌ Cleanup failed:', result.error);
    return result;
  }

  const uid = user.uid;
  console.log(`🧹 Cleaning up test wallet data for user: ${uid}`);

  const result: CleanupResult = {
    expenses: 0,
    earnings: 0,
    usage: 0,
    transactions: 0,
    messages: 0,
    total: 0,
  };

  try {
    // Wallet ledger rows tagged "test example" — SERVER-SIDE since security
    // remediation slice 2 (2026-09-15): users/{uid}/wallet is server-write-only,
    // so the deletes run in the cleanupTestWalletLedger callable on the Admin
    // SDK, for the signed-in uid only (functions/src/wallet/personalWallet.ts).
    console.log('  Deleting test wallet ledger rows (server)...');
    const cleanupLedger = httpsCallable<Record<string, never>, CleanupLedgerResult>(
      getFunctions(undefined, 'us-central1'),
      'cleanupTestWalletLedger',
    );
    const ledger = (await cleanupLedger({})).data;
    result.expenses = ledger.expenses;
    result.earnings = ledger.earnings;
    result.usage = ledger.usage;
    result.transactions = ledger.transactions;
    console.log(`  ✓ Deleted ${result.expenses} expenses, ${result.earnings} earnings, ${result.usage} usage records, ${result.transactions} transactions`);

    // Delete chat messages with "test example" tag and get remaining messages
    console.log('  Deleting test chat messages...');
    const { deletedCount, remainingMessages } = await deleteMessagesByTag(uid, TEST_TAG);
    result.messages = deletedCount;
    console.log(`  ✓ Deleted ${result.messages} chat messages`);

    // Update localStorage immediately for instant UI feedback
    // Messages are now in client format (ChatMessage[]) with proper isUser field
    if (typeof window !== 'undefined') {
      try {
        const serialized = remainingMessages.map(m => ({
          ...m,
          // Convert Date to ISO string for localStorage (messages are already in client format)
          timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
        }));
        localStorage.setItem(GLOBAL_CHAT_HISTORY_KEY, JSON.stringify(serialized));
        console.log('  ✓ Updated localStorage cache');

        // Trigger cross-tab sync via storage event
        window.dispatchEvent(new StorageEvent('storage', {
          key: GLOBAL_CHAT_HISTORY_KEY,
          newValue: JSON.stringify(serialized),
        }));

        // Dispatch custom event for IMMEDIATE UI sync (bypasses debounce)
        // This ensures the chat UI updates instantly when cleanup is triggered
        window.dispatchEvent(new CustomEvent('global-chat-force-sync', {
          detail: { messages: remainingMessages },
        }));
        console.log('  ✓ Dispatched force sync event');

        // Notify caller to update UI state immediately (pass unconverted for proper Date handling)
        options?.onMessagesCleared?.(remainingMessages);
      } catch (localStorageError) {
        console.warn('  ⚠ Could not update localStorage:', localStorageError);
      }
    }

    result.total = result.expenses + result.earnings + result.usage + result.transactions + result.messages;

    console.log(`✅ Cleanup complete! Deleted ${result.total} total records.`);
    console.log('  Summary:', result);
  } catch (error) {
    result.error = error instanceof Error ? error.message : String(error);
    console.error('❌ Cleanup failed:', result.error);
  }

  return result;
}

/**
 * Clean up only the wallet ledger's "test example" rows (no chat messages).
 * Server-side, like the full clean-up above; returns the expenses count for
 * the console helper's historical contract.
 */
export async function cleanupTestExpenses(): Promise<number> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    console.error('❌ No user logged in. Please sign in first.');
    return 0;
  }

  console.log('🧹 Cleaning up test wallet ledger rows (server)...');
  const cleanupLedger = httpsCallable<Record<string, never>, CleanupLedgerResult>(
    getFunctions(undefined, 'us-central1'),
    'cleanupTestWalletLedger',
  );
  const ledger = (await cleanupLedger({})).data;
  console.log(`✅ Deleted ${ledger.expenses} test expenses (${ledger.total} ledger rows in all).`);
  return ledger.expenses;
}

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).__cleanupTestWalletData = cleanupTestWalletData;
  (window as any).__cleanupTestExpenses = cleanupTestExpenses;

  if (process.env.NODE_ENV === 'development') {
    console.log(
      '💡 Test wallet cleanup available:\n' +
        '   await window.__cleanupTestWalletData() - Clean all test records\n' +
        '   await window.__cleanupTestExpenses() - Clean only test expenses'
    );
  }
}
